import { OpenInNew, PlayArrow, ZoomIn } from "@mui/icons-material";
import { Button, CircularProgress, Typography, useTheme } from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import Video from "yet-another-react-lightbox/plugins/video";
import "yet-another-react-lightbox/styles.css";
import { AssetType, IAsset } from "../../../../../types/NendaTypes";
import useVideoFileInformation from "../../../../hooks/useVideoFileInformation";
import { formatDurationInSeconds } from "../../../../utils/timeUtil";
import { fileSizeConverted } from "../../../../utils/fileUtil";
import { t } from "i18next";

const AssetMedia = ({
  asset,
  setMediaProperties,
}: {
  asset: IAsset;
  setMediaProperties: (values: any) => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [openImage, setOpenImage] = useState(false);
  const theme = useTheme();
  const media = asset?.thumbnailLocator || asset?.locator || "";

  const fileInfo = useVideoFileInformation(asset);
  const { isLoading, duration, fileSize, fileType, dimensions } = fileInfo;

  const isUrlAsset = asset?.type === AssetType.URL;

  useEffect(() => {
    if (isLoading) return;
    // useVideoFileInformation deliberately bails for anything that is not an
    // image or a video, so a URL asset has no duration, size or dimensions to
    // report. Sending them anyway renders "undefinedxundefined".
    if (isUrlAsset) {
      setMediaProperties({ isLoading });
      return;
    }
    setMediaProperties({
      isLoading,
      duration: formatDurationInSeconds(duration || 0),
      fileSize: fileSizeConverted(asset.fileSize || fileSize || 0),
      fileType,
      dimensions: `${dimensions?.width}x${dimensions?.height}`,
    });
  }, [
    duration,
    asset,
    dimensions.width,
    dimensions.height,
    fileSize,
    isUrlAsset,
  ]);

  if (isUrlAsset) {
    return (
      <Box sx={{ width: "30rem", margin: "1rem 0" }}>
        {/*
          The live page, rendered exactly the way a screen renders it. This is
          the definitive embeddability check: a page that refuses to be framed
          shows up blank here too, including ones that framebust via JavaScript
          and therefore pass the header check on the server.

          sandbox keeps a third-party page from navigating the portal or
          opening dialogs; allow-scripts is needed or most pages render empty.
        */}
        <Box
          component="iframe"
          src={asset?.locator}
          title={asset?.name}
          loading="lazy"
          referrerPolicy="no-referrer"
          sandbox="allow-scripts allow-same-origin allow-forms"
          sx={{
            width: "100%",
            height: "17rem",
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: "0.3rem",
            background: theme.palette.common.white,
          }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "0.5rem",
            mt: "0.5rem",
          }}
        >
          <Typography
            variant="caption"
            color="text.primary"
            sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {asset?.locator}
          </Typography>
          <Button
            size="small"
            endIcon={<OpenInNew />}
            onClick={() =>
              window.open(asset?.locator, "_blank", "noopener,noreferrer")
            }
          >
            {t("customerportal.pages.assets.create.open_page")}
          </Button>
        </Box>
        <Typography variant="caption" color="text.primary">
          {t("customerportal.pages.assets.create.preview_hint")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Hack-changing the background of the lightbox image to support transparent images (adds a checkerboard standard transparency background) */}
      <style>{`
          .yarl__slide_image {
            background-color: #fff;
            background-image: linear-gradient(45deg, #dcdcdc 25%, transparent 25%), linear-gradient(-45deg, #dcdcdc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #dcdcdc 75%), linear-gradient(-45deg, transparent 75%, #dcdcdc 75%);
            background-size: 20px 20px;
            background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
          }
        `}</style>
      <Box
        sx={{
          position: "relative",
          borderRadius: "0.3rem",
          overflow: "hidden",
          height: "17rem",
          width: "30rem",
          margin: "1rem 0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {loading && (
          <CircularProgress sx={{ position: "absolute", zIndex: 10 }} />
        )}
        <Box
          onClick={() => setOpenImage(true)}
          component={asset?.type === AssetType.VIDEO ? "video" : "img"}
          src={asset?.locator}
          sx={{
            height: "100%",
            width: "auto",
          }}
          onLoadStart={async (event) => {
            setLoading(true);
          }}
          onLoadedData={() => {
            setLoading(false);
          }}
        />
        <Box
          onClick={() => setOpenImage(true)}
          sx={{
            position: "absolute",
            inset: 0,
            opacity: loading ? 0.5 : 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
            transition: "opacity 0.2s ease",
            background: loading
              ? theme.palette.transparent.purple
              : "transparent",
            "&:hover": {
              opacity: 1,
              background: "rgba(0,0,0,0.5)",
            },
          }}
        >
          {asset?.type !== AssetType.VIDEO ? (
            <ZoomIn
              sx={{
                fontSize: "3rem",
                color: theme.palette.common.white,
              }}
            />
          ) : (
            <PlayArrow
              sx={{
                fontSize: "3rem",
                color: theme.palette.common.white,
              }}
            />
          )}
        </Box>
      </Box>
      <Box onClick={() => setOpenImage(false)}>
        <Lightbox
          plugins={asset?.type === "video" ? [Video] : []}
          video={{ autoPlay: true, playsInline: true }}
          open={openImage}
          close={() => setOpenImage(false)}
          render={{
            buttonPrev: () => null,
            buttonNext: () => null,
          }}
          slides={
            asset?.type === "video"
              ? [
                  {
                    type: "video",
                    width: 1280,
                    height: 720,
                    poster: media,
                    sources: [
                      {
                        src: asset?.locator,
                        type: "video/mp4",
                      },
                    ],
                  },
                ]
              : [
                  {
                    src: asset?.locator,
                    width: 1280,
                    height: 720,
                    type: "image",
                  },
                ]
          }
        />
      </Box>
    </Box>
  );
};

export default AssetMedia;
